import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Plus, Trash2, Loader2, Edit2, Tag as TagIcon } from 'lucide-react';
import Cookies from 'js-cookie';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Tags() {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTag, setNewTag] = useState({ name: '' });
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ name: '' });
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    try {
      const token = Cookies.get('admin_token');
      const response = await axios.get(`${API}/master/tags`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setTags(response.data.data || []);
    } catch (err) {
      toast.error('Failed to load tags');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newTag.name.trim()) return;

    setAdding(true);
    try {
      const token = Cookies.get('admin_token');
      await axios.post(`${API}/master/tags`, 
        { name: newTag.name },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success('Tag added successfully');
      setNewTag({ name: '' });
      setOpen(false);
      fetchTags();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add tag');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this tag?')) return;

    try {
      const token = Cookies.get('admin_token');
      await axios.delete(`${API}/master/tags/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Tag deleted');
      fetchTags();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete tag');
    }
  };

  const handleEdit = (tag) => {
    setEditingId(tag.id);
    setEditData({ name: tag.name });
    setEditOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editData.name.trim()) return;

    try {
      const token = Cookies.get('admin_token');
      await axios.put(`${API}/master/tags/${editingId}`, 
        { name: editData.name },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success('Tag updated successfully');
      setEditOpen(false);
      setEditingId(null);
      fetchTags();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update tag');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading mb-2">Tags</h1>
          <p className="text-muted-foreground">Manage tags for content organization</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="editorial">
              <Plus className="h-4 w-4 mr-2" /> Add Tag
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Tag</DialogTitle>
              <DialogDescription>Create a new tag for content tagging</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAdd}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="tag">Tag Name *</Label>
                  <Input
                    id="tag"
                    value={newTag.name}
                    onChange={(e) => setNewTag({...newTag, name: e.target.value})}
                    placeholder="e.g., Innovation, Funding, Growth"
                    disabled={adding}
                    required
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={adding}>
                  Cancel
                </Button>
                <Button type="submit" disabled={adding || !newTag.name.trim()}>
                  {adding ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Adding...</>
                  ) : (
                    'Add Tag'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit Tag Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Tag</DialogTitle>
            <DialogDescription>Update tag name</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-tag">Tag Name *</Label>
                <Input
                  id="edit-tag"
                  value={editData.name}
                  onChange={(e) => setEditData({...editData, name: e.target.value})}
                  placeholder="e.g., Innovation, Funding, Growth"
                  required
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!editData.name.trim()}>
                Update Tag
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Tags List */}
      <Card>
        <CardHeader>
          <CardTitle>All Tags ({tags.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            </div>
          ) : tags.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">No tags added yet</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tags.map((tag) => (
                <div
                  key={tag.id}
                  className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-accent/50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <TagIcon className="h-4 w-4 text-primary" />
                    <p className="font-medium">{tag.name}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(tag)}
                    >
                      <Edit2 className="h-4 w-4 text-primary" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(tag.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
